import { Grid, FormControl, Select, MenuItem, Box, Typography } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { getNextVisitDateDifference, formatTimeSlotInClinicTimezone } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/utils/nextVisitRosterCalendarUtils'
import { getCurrencyObject, getPriceStringWithCurrency, getSubscriptionPlanById } from 'src/mentor/helpers/utilityFunctions'
import { CustomInput } from 'src/consumer/components/helperComponents'
import { SESSION_PLATFORM_OFFLINE, SESSION_PLATFORM_ONLINE, displayNamesOfEventTypes } from 'src/consumer/constants'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'

export default function NextVisitHoldSlotConfirmationView({
  selectedSlot,
  selectedDate,
  bookingDate,
  currentEventTypeObject,
  bookingFormData,
  handleBookingFormChange,
  mentorProfile,
  selectedEventTypeId,
  setSelectedEventTypeId,
  clinicLocation = null, // Clinic location for timezone
}) {
  const [subscriptionPlans, setSubscriptionPlans] = useState({}) // Map of eventTypeId -> subscription plan data
  const [initialFee, setInitialFee] = useState(null) // Track initial fee for display message
  const previousEventTypeIdRef = useRef(null) // Track previous event type to detect changes

  // Get available event types for selection
  // Filter by eventType and clinicBrandId matching currentEventTypeObject
  const availableEventTypes = useMemo(() => {
    if (!mentorProfile?.eventTypes) return []
    
    let filtered = mentorProfile.eventTypes.filter(et => et.enabled !== false)
    
    // Filter by eventType if currentEventTypeObject has a type
    if (currentEventTypeObject?.type) {
      filtered = filtered.filter(et => et.type === currentEventTypeObject.type)
    }
    
    // Filter by clinicBrandId if currentEventTypeObject or clinicLocation has clinicBrandId
    const targetClinicBrandId = currentEventTypeObject?.clinicBrandId || clinicLocation?.clinicBrandId
    if (targetClinicBrandId) {
      filtered = filtered.filter(et => {
        // Check if event type has clinicBrandId that matches, or if it doesn't have one (global)
        return !et.clinicBrandId || et.clinicBrandId === targetClinicBrandId
      })
    }
    
    return filtered
  }, [mentorProfile, currentEventTypeObject, clinicLocation])

  // Fetch subscription plans for all available event types
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      const plansMap = {}
      
      // Fetch plans for all event types in parallel
      const fetchPromises = availableEventTypes.map(async (eventType) => {
        const planId = eventType?.subscriptionPlanId || eventType?.pricingPlanId
        if (planId && !eventType?.free) {
          try {
            const response = await getSubscriptionPlanById(planId)
            if (response?.status === 200 && response?.data) {
              plansMap[eventType.uid] = response.data
            }
          } catch (error) {
            console.error(`Error fetching subscription plan for event type ${eventType.uid}:`, error)
          }
        }
      })
      
      await Promise.all(fetchPromises)
      setSubscriptionPlans(plansMap)
    }

    if (availableEventTypes.length > 0) {
      fetchSubscriptionPlans()
    }
  }, [availableEventTypes])

  // Initialize fee on first load
  useEffect(() => {
    if (!selectedEventTypeObject || previousEventTypeIdRef.current !== null) return
    
    const subscriptionPlan = subscriptionPlans[selectedEventTypeObject.uid]
    
    if (subscriptionPlan) {
      const fee = subscriptionPlan?.finalPrice || subscriptionPlan?.price
      const currency = subscriptionPlan?.currency || bookingFormData?.currency || 'inr'
      
      if (fee !== undefined && fee !== null) {
        setInitialFee(fee)
        handleBookingFormChange({
          target: {
            id: 'fee',
            value: fee.toString(),
          },
        })
        if (currency !== bookingFormData?.currency) {
          handleBookingFormChange({
            target: {
              id: 'currency',
              value: currency,
            },
          })
        }
      }
    } else if (selectedEventTypeObject?.free) {
      setInitialFee(0)
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: '0',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionPlans, selectedEventTypeObject?.uid])

  // Get selected event type object
  const selectedEventTypeObject = useMemo(() => {
    return availableEventTypes.find(et => et.uid === selectedEventTypeId) || currentEventTypeObject
  }, [availableEventTypes, selectedEventTypeId, currentEventTypeObject])

  // Update fee when event type changes
  useEffect(() => {
    if (!selectedEventTypeObject) return
    
    const currentEventTypeId = selectedEventTypeObject.uid
    
    // Only update if event type actually changed
    if (previousEventTypeIdRef.current === currentEventTypeId) {
      return
    }
    
    previousEventTypeIdRef.current = currentEventTypeId
    
    const subscriptionPlan = subscriptionPlans[currentEventTypeId]
    
    // Get fee from subscription plan
    if (subscriptionPlan) {
      const fee = subscriptionPlan?.finalPrice || subscriptionPlan?.price
      const currency = subscriptionPlan?.currency || bookingFormData?.currency || 'inr'
      
      if (fee !== undefined && fee !== null) {
        // Set initial fee if not set yet
        if (initialFee === null) {
          setInitialFee(fee)
        }
        
        // Update booking form data with fee from subscription plan
        handleBookingFormChange({
          target: {
            id: 'fee',
            value: fee.toString(),
          },
        })
        
        // Update currency if different
        if (currency !== bookingFormData?.currency) {
          handleBookingFormChange({
            target: {
              id: 'currency',
              value: currency,
            },
          })
        }
      }
    } else if (selectedEventTypeObject?.free) {
      // If event type is free, set fee to 0
      if (initialFee === null) {
        setInitialFee(0)
      }
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: '0',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventTypeObject?.uid, subscriptionPlans])

  // Format slot time in clinic timezone
  const formattedSlotTime = useMemo(() => {
    if (!selectedSlot) return null
    
    // Use clinicLocation from slot if available, otherwise use prop
    const location = selectedSlot.clinicLocation || clinicLocation
    
    // If slot has start/end times, format them
    if (selectedSlot.start && selectedSlot.end) {
      return formatTimeSlotInClinicTimezone(
        selectedSlot.start,
        selectedSlot.end,
        location,
        selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30
      )
    }
    
    // If slot has label, try to extract times or use label as-is
    // But prefer formatted version if we can get it
    if (selectedSlot.label) {
      // If label already includes timezone info, use it
      if (selectedSlot.labelWithTz) {
        return { label: selectedSlot.labelWithTz, labelWithTz: selectedSlot.labelWithTz }
      }
      // Otherwise, if we have start time, format it
      if (selectedSlot.start) {
        return formatTimeSlotInClinicTimezone(
          selectedSlot.start,
          selectedSlot.end,
          location,
          selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30
        )
      }
      // Fallback to original label
      return { label: selectedSlot.label, labelWithTz: selectedSlot.label }
    }
    
    return null
  }, [selectedSlot, clinicLocation, selectedEventTypeObject, currentEventTypeObject])

  return (
    <div style={{ padding: '20px 0', height: '100%', flexGrow: 1 }}>
      <div
        className="d-flex justify-content-center gap-5"
        style={{
          textAlign: 'center',
          padding: '16px 16px',
          backgroundColor: '#e2dfff',
          borderRadius: '12px',
          border: '2px solid #a167f4',
          margin: '0 20px 30px',
        }}
      >
        <div>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🎯</div>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#666',
              marginBottom: '8px',
            }}
          >
            Confirm The Slot & Payment Details
          </div>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              margin: '12px 0',
              color: 'rgb(141, 68, 173)',
            }}
          >
            {formattedSlotTime?.labelWithTz || formattedSlotTime?.label || selectedSlot?.label}
          </div>
          <div style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
            {selectedDate?.format('dddd, MMMM D, YYYY')} (
            {getNextVisitDateDifference(selectedDate, bookingDate)})
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            fontSize: '14px',
            color: '#666',
            padding: '12px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            marginTop: '20px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>📋 Booking Details:</div>
          <div>Duration: {selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30} minutes</div>
          <div>Date: {selectedDate?.format('DD/MM/YYYY')}</div>
          <div>Time: {formattedSlotTime?.labelWithTz || formattedSlotTime?.label || selectedSlot?.label}</div>
          <div>Payment: Link will be sent to patient</div>
        </div>
      </div>

      {/* Event Type Selector */}
      {availableEventTypes.length > 1 && (
        <Box
          sx={{
            margin: '0 20px 20px',
            padding: '16px',
            backgroundColor: 'rgba(139, 92, 246, 0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <CalendarMonthIcon sx={{ fontSize: '18px', color: '#7c3aed' }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6b21a8' }}>
              Appointment Type
            </Typography>
          </Box>
          <FormControl fullWidth>
            <Select
              value={selectedEventTypeId || currentEventTypeObject?.uid || ''}
              onChange={(e) => setSelectedEventTypeId(e.target.value)}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.25)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.4)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.6)',
                },
              }}
            >
              {availableEventTypes.map((eventType) => {
                // Get price from subscription plan
                const subscriptionPlan = subscriptionPlans[eventType.uid]
                let priceString = null
                
                if (subscriptionPlan) {
                  // Get price from subscription plan
                  const price = subscriptionPlan?.finalPrice || subscriptionPlan?.price
                  const currency = subscriptionPlan?.currency || 'inr'
                  if (price) {
                    priceString = getPriceStringWithCurrency(price, currency)
                  }
                } else if (eventType?.free) {
                  priceString = 'Free'
                }
                
                return (
                  <MenuItem key={eventType.uid} value={eventType.uid}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                      {eventType.meetingSrc === SESSION_PLATFORM_OFFLINE ? (
                        <LocationOnIcon sx={{ fontSize: '18px', color: '#d32f2f' }} />
                      ) : (
                        <VideocamIcon sx={{ fontSize: '18px', color: '#1976d2' }} />
                      )}
                      <Typography sx={{ flex: 1, color: '#6b21a8', fontWeight: 500 }}>
                        {eventType.title || displayNamesOfEventTypes?.[eventType.type] || eventType.type}
                        {priceString && (
                          <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 600, color: '#4caf50' }}>
                            - {priceString}
                          </span>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                        ({eventType.durationInMins || 30} mins)
                      </Typography>
                    </Box>
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Payment Details Form - Always Visible */}
      <Box
        sx={{
          margin: '0 20px 20px',
          padding: '16px',
          backgroundColor: 'rgba(139, 92, 246, 0.05)',
          borderRadius: '10px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
        }}
      >
        <Box sx={{ marginBottom: '12px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6b21a8', marginBottom: '8px' }}>
            Fee Details
          </Typography>
          {initialFee !== null && (
            <Typography 
              sx={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                fontStyle: 'italic',
                marginBottom: '12px',
              }}
            >
              Fee for current session was {getPriceStringWithCurrency(initialFee, bookingFormData?.currency || 'inr')}
            </Typography>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
              <CustomInput
                id="fee"
                conponentContainerStyles={{ padding: '0px', width: '100%' }}
                inputAnsMessageContainerStyles={{ padding: '0px 0px 0px 8px', gap: '0px' }}
                value={bookingFormData?.fee ?? ''}
                preFixText={getCurrencyObject(bookingFormData?.currency)?.symbol}
                label={`Fee`}
                type="number"
                labelPlacement="horizontal"
                onChange={handleBookingFormChange}
                placeholder="Enter fee amount"
              />
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}
